import { TriggerType, useLeadStatuses } from "@/api/app-configuration.api";
import {
  getCompanyReportsQueryOptions,
  getCompanyResumeQueryOptions,
  getPerformanceObservationsQueryOptions,
} from "@/api/company.api";
import {
  contactsApi,
  createHistoryContactsQueryOptions,
  createLeadContactQueryOptions,
  HistoryResponse,
  type HistoryContact,
} from "@/api/contacts.api";
import { AnimateChangeInHeight } from "@/components/AnimateHeight";
import { Card } from "@/components/Card";
import { Button } from "@/components/generic/Button";
import { FocusableText } from "@/components/generic/FocusableText";
import { Heading } from "@/components/generic/Heading";
import { Separator } from "@/components/generic/Separator";
import TemplateText from "@/components/generic/TemplateText";
import { Tooltip } from "@/components/generic/Tooltip";
import { PurchaseContactDialog } from "@/components/lead/PurchaseContactDialog";
import { PurchaseLiveNumberDialog } from "@/components/lead/PurchaseLiveNumberDialog";
import { Relations } from "@/components/lead/Relations";
import { ReportErrorDialog } from "@/components/lead/ReportErrorDialog";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { companyTypesMap } from "@/data/company";
import { danishCountryPartsMap } from "@/data/countryparts";
import {
  filterCategories,
  groupFilterGroupsByCategory,
  groupFiltersByFilterGroup,
} from "@/data/filters/config";
import { findIndustryCodeDeep, industryCodes } from "@/data/industry-codes";
import { municipalitiesMap } from "@/data/municipalities";
import { regionsMap } from "@/data/regions";
import { useScrollAtBottom } from "@/hooks/use-scroll-at-bottom";
import { trialText, useTrialPeriod } from "@/hooks/use-trial-period";
import { useLeadNavigation } from "@/providers/LeadNavigationProvider";
import { useScreenSize } from "@/providers/ScreenSizeProvider";
import { hasInsightsAccess, isAdmin } from "@/services/auth.service";
import { matchedTargetGroupFiltersQuery } from "@/services/matched-target-groups.service";
import {
  PhoneNumbersResponse,
  Type,
  usePhoneNumber,
  usePurchaseLiveNumbers,
} from "@/services/phone.services";
import { getWebsiteQueryOptions } from "@/services/website.service";
import {
  ContactResponse,
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  LeadResponse,
  QueryFilter,
} from "@/types/campaign";
import { VIRKReport } from "@/types/company";
import { mapAndExclude } from "@/utils/array-utils";
import { formatDate, formatDateTime, formatSince } from "@/utils/dates";
import { censorEmail } from "@/utils/email-util";
import lassoUrls from "@/utils/lasso-urls";
import {
  formatMoney,
  formatPercentage,
  formatReportMoney,
} from "@/utils/money";
import {
  censorPhoneNumber,
  formatNumber,
  formatPhoneNumber,
} from "@/utils/number-utils";
import { abbreviate, pluralize } from "@/utils/string-utils";
import { cleanWebsiteUrl, formatAddress } from "@/utils/virk-utils";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { AnimatePresence, HTMLMotionProps, motion } from "framer-motion";
import {
  AlarmClock,
  Ban,
  Captions,
  CheckCircle,
  ChevronDown,
  CircleCheckBig,
  Copy,
  Download,
  Handshake,
  Mail,
  MapPin,
  MessageCircleHeart,
  OctagonAlert,
  Phone,
  RefreshCw,
  ShieldCheck,
  ShieldX,
  ThumbsDown,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import normalizeUrl from "normalize-url";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Focusable, useFocusable } from "react-aria";
import { MenuTrigger, Button as RACButton } from "react-aria-components";

import LinkedInBlack from "@/assets/InBug-Black.png";
import LinkedInWhite from "@/assets/InBug-White.png";
import { AlertModal } from "@/components/generic/AlertModal";
import { ThemeContext } from "@/providers/theme-context";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { useCopyToClipboard } from "usehooks-ts";
import { liveNumberFindCb } from "../../utils/live-number";

type KeyFigureKeys =
  | "grossResult"
  | "equity"
  | "resultBeforeTax"
  | "result"
  | "ebitda"
  | "profitFromOrdinaryOperatingActivities"
  | "liquidityRatio"
  | "solvencyRatio";

type FinancialReports<K extends string> = {
  report: VIRKReport;
  trends: Partial<
    Record<
      K,
      {
        unit: string;
        value: number;
        trend: "up" | "down" | "neutral";
      }
    >
  >;
};

export const Route = createFileRoute("/player/$id")({
  component: RouteComponent,
  params: {
    parse: (params: { id: number | string }) => params,
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const params = Route.useParams();
  const { user } = context;

  const trialPeriod = useTrialPeriod();

  const { data: company } = context.useCompanyQuery();

  const { getLeadStatusLabel } = useLeadStatuses();

  const { leads } = context.useLeads();
  const { data: lead } = context.useLeadQuery();

  const leadsIndexesById = useMemo(
    () => new Map(leads.map((lead, index) => [lead.id, index])),
    [leads],
  );

  const leadsIndex = useMemo(
    () =>
      (context.leadView && leadsIndexesById.get(context.leadView.leadId)) ?? 0,
    [context.leadView, leadsIndexesById],
  );

  const previousLeadsIndex = useMemo(
    () =>
      context.leadView?.previousLeadId !== undefined
        ? (leadsIndexesById.get(context.leadView.previousLeadId) ?? leadsIndex)
        : leadsIndex,
    [context.leadView?.previousLeadId, leadsIndex, leadsIndexesById],
  );

  const companyLassoId = useMemo(
    () =>
      context.view.type === "company"
        ? context.view.company.lassoId
        : context.view.simpleLead.lassoId,
    [context.view],
  );

  const leadId = useMemo(
    () =>
      context.view.type === "company"
        ? context.leadView?.simpleLead.id
        : context.view.simpleLead.id,
    [context.view, context.leadView],
  );

  const { data: websites } = useQuery(getWebsiteQueryOptions(companyLassoId));
  const companyWebsite = useMemo(() => {
    if (!websites || websites.urls.length === 0) return null;
    const sortedUrls = websites.urls.sort((a, b) => {
      return (
        new Date(b.verifiedAt).getTime() - new Date(a.verifiedAt).getTime()
      );
    });
    return cleanWebsiteUrl(sortedUrls[0].url);
  }, [websites]);

  const companyResumeQueryOptions =
    getCompanyResumeQueryOptions(companyLassoId);

  const matchedTargetGroupFiltersQueryOptions = matchedTargetGroupFiltersQuery(
    context.leadView?.simpleLead.id,
  );

  const companyReportsQueryOptions =
    getCompanyReportsQueryOptions(companyLassoId);

  const observationsQueryOptions =
    getPerformanceObservationsQueryOptions(companyLassoId);

  const { data: campaigns } = context.useSimpleCampaignsQuery();

  const {
    data: companyResume,
    isFetched: resumeFetched,
    isFetching: isResumeFetching,
    isError: isResumeError,
    refetch: refetchResume,
  } = useQuery(companyResumeQueryOptions);

  const { data: observations } = useQuery(observationsQueryOptions);

  const { data: companyReports } = useQuery(companyReportsQueryOptions);

  const {
    data: targetGroups,
    isError: isTargetGroupError,
    isPending: isTargetGroupPending,
    isFetching: isTargetGroupFetching,
  } = useQuery(matchedTargetGroupFiltersQueryOptions);

  const { data: users } = context.useUsersQuery();

  const { data: phoneNumbersRaw } = usePhoneNumber(
    leadId?.toString() ?? "",
    companyLassoId,
  );

  const phoneNumbers = useMemo(
    () =>
      phoneNumbersRaw &&
      (trialPeriod
        ? {
            ...phoneNumbersRaw,
            numbers: phoneNumbersRaw.numbers.map((number) => ({
              ...number,
              phoneNumber: censorPhoneNumber(number.phoneNumber),
            })),
          }
        : phoneNumbersRaw),
    [trialPeriod, phoneNumbersRaw],
  );

  const { data: campaign } = context.useCampaignQuery();

  const { mutate: purchaseLiveNumbers } = usePurchaseLiveNumbers(
    leadId?.toString() ?? "",
    companyLassoId,
  );

  const handlePurchaseLiveNumbers = useCallback(() => {
    purchaseLiveNumbers(undefined, {
      onError: (error) => {
        if (error instanceof Error && error.name === "paymentRequired") {
          setIsOutOfCreditsModalOpen(true);
        }
      },
    });
  }, [purchaseLiveNumbers]);

  const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);

  const phoneNumber = useMemo(() => {
    if (!phoneNumbers?.numbers) return undefined;
    const nonObfuscated = phoneNumbers.numbers.find(
      (num) => liveNumberFindCb(num) && !num.obfuscated,
    );
    if (nonObfuscated) return nonObfuscated;
    return phoneNumbers.numbers.find(liveNumberFindCb);
  }, [phoneNumbers]);

  const [isOutOfCreditsModalOpen, setIsOutOfCreditsModalOpen] = useState(false);
  const [outOfCreditsShownForContacts, setOutOfCreditsShownForContacts] =
    useState(new Set<string>());

  const historyQueryOptions = useMemo(
    () =>
      companyLassoId ? createHistoryContactsQueryOptions(companyLassoId) : null,
    [companyLassoId],
  );

  const { data: historyData } = useQuery(
    historyQueryOptions ?? {
      queryKey: ["history-contacts-disabled"],
      queryFn: () => Promise.resolve([]),
      enabled: false,
    },
  );

  // TODO : We need to support multiple history sites
  const historyContacts = useMemo(
    () => historyData?.[0]?.contacts ?? [],
    [historyData],
  );

  const preferredContactId = useMemo(
    () => context.leadView?.simpleLead.contact?.id,
    [context.leadView?.simpleLead.contact?.id],
  );

  const queryClient = useQueryClient();
  const [contactIdsToLoad, setContactIdsToLoad] = useState(new Set<string>());
  const [
    contactIdsWithPurchasedContactInfo,
    setContactIdsWithPurchasedContactInfo,
  ] = useState(new Set<string>());
  const [refreshingContactIds, setRefreshingContactIds] = useState(
    new Set<string>(),
  );

  const contactQueriesOptionsMap = useMemo(() => {
    const historyContactIds = new Set(historyContacts.map((c) => c.id));

    if (preferredContactId) historyContactIds.add(preferredContactId);

    return new Map(
      [...historyContactIds].map((historyContactId) => [
        historyContactId,
        {
          ...createLeadContactQueryOptions(
            lead?.lead.id.toString() ?? "",
            historyContactId,
            contactIdsWithPurchasedContactInfo.has(historyContactId),
            {
              enabled:
                !!lead?.lead.id && contactIdsToLoad.has(historyContactId),
              placeholderData: false,
            },
          ),
        },
      ]),
    );
  }, [
    contactIdsToLoad,
    historyContacts,
    lead?.lead.id,
    preferredContactId,
    contactIdsWithPurchasedContactInfo,
  ]);

  const historyContactIds = useMemo(
    () => [...contactQueriesOptionsMap.keys()],
    [contactQueriesOptionsMap],
  );

  const contactQueriesOptions = useMemo(
    () => [...contactQueriesOptionsMap.values()],
    [contactQueriesOptionsMap],
  );

  const contactQueries = useQueries({
    queries: contactQueriesOptions,
  });

  const contactQueriesById = useMemo(
    () =>
      new Map(
        historyContactIds.map((id, index) => [id, contactQueries[index]]),
      ),
    [contactQueries, historyContactIds],
  );

  const loadingContactIds = useMemo(
    () =>
      new Set(
        mapAndExclude(historyContactIds, (exclude, id) =>
          contactQueriesById.get(id)?.isFetching ? id : exclude,
        ),
      ),
    [historyContactIds, contactQueriesById],
  );

  const purchaseContact = useCallback((contactId: string) => {
    setContactIdsWithPurchasedContactInfo((prevIds) => {
      if (prevIds.has(contactId)) return prevIds;
      return new Set(prevIds).add(contactId);
    });
  }, []);

  const refreshContact = useCallback(
    async (contactId: string) => {
      const leadIdStr = lead?.lead.id.toString() ?? "";
      if (!leadIdStr) return;
      setRefreshingContactIds((prev) => new Set(prev).add(contactId));
      try {
        const hasPurchasedInfo =
          contactIdsWithPurchasedContactInfo.has(contactId);
        const result = await contactsApi.get(
          leadIdStr,
          contactId,
          hasPurchasedInfo,
          undefined,
          true,
        );
        if (result) {
          const cacheKey = hasPurchasedInfo
            ? "with-contact-info"
            : "without-contact-info";
          queryClient.setQueryData(
            ["lead-contact", leadIdStr, contactId, cacheKey],
            result,
          );
          queryClient.invalidateQueries({
            queryKey: ["lead", leadId],
          });
        }
      } catch {
        toast.error("Der skete en fejl ved genindlæsning af kontakten.");
      } finally {
        setRefreshingContactIds((prev) => {
          const next = new Set(prev);
          next.delete(contactId);
          return next;
        });
      }
    },
    [lead?.lead.id, contactIdsWithPurchasedContactInfo, queryClient],
  );

  const loadedContactsMap = useMemo(
    () =>
      new Map(
        mapAndExclude(
          [...contactQueriesById],
          (exclude, [contactId, contactQuery]) =>
            contactQuery.data ? [contactId, contactQuery.data] : exclude,
        ),
      ),
    [contactQueriesById],
  );

  const loadedContacts = useMemo<ContactResponse[]>(() => {
    return [...loadedContactsMap.values()];
  }, [loadedContactsMap]);

  const preferredContactQuery = useMemo(() => {
    const id = context.leadView?.simpleLead.contact?.id;
    if (!id) return;
    return contactQueriesById.get(id);
  }, [contactQueriesById, context.leadView?.simpleLead.contact?.id]);

  const isFetchingPreferredContact = preferredContactQuery?.isFetching ?? false;
  const preferredContact = preferredContactQuery?.data;
  const preferredContactError = preferredContactQuery?.error;

  useEffect(() => {
    const contactIdsToLoad = new Set<string>();
    if (preferredContactId) contactIdsToLoad.add(preferredContactId);

    setContactIdsToLoad(contactIdsToLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.leadView?.leadId]);

  const [areFiltersScrolled, setAreFiltersScrolled] = useState(false);

  const [expandResume, setExpandResume] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const resumeContentRef = useRef<HTMLDivElement>(null);
  const [truncatedHeight, setTruncatedHeight] = useState<number>(0);

  const [, copy] = useCopyToClipboard();
  // Define how many lines to show when truncated
  const MAX_VISIBLE_LINES = 4;

  // Add effect to check if content needs truncation and calculate dynamic height
  useEffect(() => {
    if (resumeContentRef.current && companyResume?.content) {
      // Get computed style to determine line height
      const computedStyle = window.getComputedStyle(resumeContentRef.current);
      const lineHeight =
        parseInt(computedStyle.lineHeight) ||
        parseInt(computedStyle.fontSize) * 1.2; // Fallback if lineHeight is not set

      // Calculate maximum height based on line height and desired number of visible lines
      const maxHeight = lineHeight * MAX_VISIBLE_LINES;
      setTruncatedHeight(maxHeight);

      // Compare scrollHeight to the calculated truncated height
      setNeedsTruncation(resumeContentRef.current.scrollHeight > maxHeight);
    }
  }, [companyResume?.content]);

  useEffect(() => {
    setExpandResume(false);
  }, [params.id]);

  const usersFlattened = users?.pages.flatMap((page) => page.results);

  const formattedEventTriggers = useMemo(() => {
    return context.appConfiguration!.triggerTypes.reduce(
      (acc, trigger) => {
        acc[trigger.event] = trigger;
        return acc;
      },
      {} as Record<string, TriggerType>,
    );
  }, [context.appConfiguration]);

  const simpleCampaign = useMemo(() => {
    return campaigns?.find((c) => c.id === lead?.lead.campaignId);
  }, [campaigns, lead]);

  const filterNames = useMemo(
    () =>
      groupFilterGroupsByCategory(
        groupFiltersByFilterGroup((targetGroups ?? []) as QueryFilter[]), // TODO: dangerous type assertion, fix
      ),
    [targetGroups],
  );

  const campaignConfiguration = useMemo(() => {
    return campaign?.configurations.find(
      (c) => c.id === lead?.lead.configurationId,
    );
  }, [campaign, lead]);

  const leadTypeColors = {
    Guld: "text-theme-gold",
    Sølv: "text-theme-silver",
    Bronze: "text-theme-bronze",
  };

  const handleCopy = (text: string) => {
    copy(text)
      .then(() => {
        toast("Nummer kopieret til udklipsholderen");
      })
      .catch((error) => {
        console.error("Copy failed", error);
        toast.error("Kopiering mislykkedes", {
          description: "Tjek venligst din browserindstillinger",
        });
      });
  };

  const formatFilterValues = (
    filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[],
  ) => {
    const FILTERED_FILTERS = [
      "hasDigitalMarketing",
      "hasCrmSystem",
      "hasECommerce",
      "hasCmsSystem",
    ];

    return filters
      .filter((f) => !f.filterName || !FILTERED_FILTERS.includes(f.filterName))
      .map((filter, _, array) => {
        if (filter.operator === "DepartmentSize") {
          const groups = filter.groups;
          if (!groups || groups.length === 0)
            return {
              filterName: filter.filterName,
              values: "",
            };

          const descriptions = groups.map((group) => {
            const values = group.values;
            const department = values?.[0];
            if (group.operator === "GreaterEqual") {
              return `Mindst ${values?.[1]} kontaktperson(er) i ${department}`;
            } else if (group.operator === "LessEqual") {
              return `Højst ${values?.[1]} kontaktperson(er) i ${department}`;
            } else if (group.operator === "Between") {
              return `Mellem ${values?.[1]}-${values?.[2]} kontaktperson(er) i ${department}`;
            }
            return "";
          });

          return {
            filterName: filter.filterName,
            values: descriptions.join(", "),
          };
        }
        if (!filter.values)
          return {
            filterName: filter.filterName,
            values: "",
          };

        // Since we manually go look for the specific tech filter, we need to ignore the "tech-" filter here
        if (
          filter.filterName.startsWith("tech-") &&
          !filter.filterName.startsWith("tech-has")
        )
          return {
            filterName: filter.filterName,
            values: "",
          };

        // Format values based on filter name and type
        let formattedValues = [...filter.values];

        // TODO: WIP logic that uses the filter group display value to format values instead of hardcoding logic here
        // TODO: to use it for all filters, they all need to handle logic for ignoreOperator so that they render correctly in this context
        // const foundFilter = getFilterGroup(filter.filterName);

        // if (!foundFilter)
        //   throw new Error(
        //     `Filter group not found for filter "${filter.filterName}"`,
        //   );

        // formattedValues = [
        //   foundFilter.filterGroup.DisplayValue({
        //     filters: [filter],
        //     ignoreOperator: true,
        //   }) as string,
        // ];

        // console.debug("formattedValues", formattedValues, filter);

        // return {
        //   filterName: filter.filterName,
        //   values: formattedValues.filter(Boolean).join(", "),
        // };

        // Check if it's a region filter
        if (filter.filterName === "geography-region") {
          formattedValues = filter.values.map((value) => {
            return regionsMap.get(Number(value).toString()) || value;
          });
        }
        // Check if it's a municipality filter
        else if (filter.filterName === "geography-municipality") {
          formattedValues = filter.values.map((value) => {
            return municipalitiesMap.get(value) || value;
          });
        }
        // Check if postal code filter, don't format as a number
        else if (filter.filterName === "geography-postal-code") {
          formattedValues = filter.values.map((value) => String(value));
        }
        // Check if it's a countrypart filter
        else if (filter.filterName === "geography-countrypart") {
          formattedValues = filter.values.map((value) => {
            return danishCountryPartsMap.get(value)?.title || value;
          });
        }
        // Check if filter is address change event
        else if (filter.filterName === "geography-address-change") {
          formattedValues = filter.values.map(
            // TODO: bad typing here, fix
            (value) =>
              `${formatDate((value as unknown as { time: string }).time)}`,
          );
        }
        // Format industry codes
        else if (filter.filterName === "basic-industry") {
          formattedValues = filter.values.map((value) => {
            return findIndustryCodeDeep(industryCodes, value)?.name || value;
          });
        }
        // Check if it's a boolean filter
        else if (
          filter.values.length === 1 &&
          typeof filter.values[0] === "boolean"
        ) {
          formattedValues = filter.values.map((value) =>
            Boolean(value) === true ? "Ja" : "Nej",
          );
        }
        // Check if it's a null filter
        else if (filter.filterName === "basic-exclude-holding") {
          if (filter.values.length === 0) {
            formattedValues = ["Nej"];
          } else if (
            filter.values[0] === "true" ||
            (typeof filter.values[0] === "boolean" && filter.values[0] === true)
          ) {
            formattedValues = ["Ja"];
          }
        }
        // Check if it's a company type filter
        else if (filter.filterName === "basic-company-type") {
          formattedValues = filter.values.map((value) => {
            return companyTypesMap.get(Number(value)) || String(value);
          });
        }
        // Format company age or established year
        else if (filter.filterName?.toLowerCase().includes("date")) {
          formattedValues = filter.values.map((value) => {
            return formatDate(value);
          });
        }
        // Check if has email filter
        else if (filter.filterName === "contact-has-email") {
          if (filter.values.length >= 1) {
            formattedValues = ["Ja"];
          } else {
            formattedValues = ["Nej"];
          }
        }
        // Check if is liquid filter
        else if (filter.filterName === "performance-is-liquid") {
          if (filter.values.length >= 1) {
            formattedValues = ["Ja"];
          } else {
            formattedValues = ["Nej"];
          }
        }
        // Check if is ROI filter
        else if (filter.filterName === "economy-return-on-assets-value") {
          if (filter.values.length >= 1) {
            formattedValues = filter.values.map((v) =>
              formatPercentage(parseInt(v, 10) / 100),
            );
          }
        }
        // Check if filter is label
        else if (
          filter.filterName.startsWith("website-label") ||
          filter.filterName.startsWith("hasAiLabel")
        ) {
          formattedValues = filter.values.length >= 1 ? ["Ja"] : ["Nej"];
        }
        // Check if filter is technology check filter
        else if (filter.filterName.startsWith("tech-has")) {
          // Find the specific filter for the technology to get the actual values and not just "has" or "not has"
          // This also ensures that we don't end up showing "Ja, {tech}" when a specific technology is selected
          const specificTech = array.find(
            (f) => f.filterName === filter.filterName.replace("-has-", "-"),
          );
          if (!specificTech)
            formattedValues = filter.values.length >= 1 ? ["Ja"] : ["Nej"];
          else {
            formattedValues = specificTech.values?.map((value) => value) || [];
          }
        }
        // Handle numeric values with proper formatting
        else if (
          filter.values.length > 0 &&
          (/^[0-9.,]+$/.test(filter.values[0]) ||
            filter.filterName?.toLowerCase().includes("amount") ||
            filter.filterName?.toLowerCase().includes("count") ||
            filter.filterName?.toLowerCase().includes("number") ||
            filter.filterName?.toLowerCase().includes("employees") ||
            filter.filterName?.toLowerCase().includes("revenue") ||
            filter.filterName?.toLowerCase().includes("profit"))
        ) {
          formattedValues = filter.values
            .map((value) => {
              // For currency values
              if (
                filter.filterName?.startsWith("economy") &&
                filter.filterName?.endsWith("value")
              ) {
                return formatMoney(Number(value));
              } else if (
                (filter.filterName?.startsWith("economy") &&
                  filter.filterName?.endsWith("change")) ||
                filter.filterName?.endsWith("basic-employees-change")
              ) {
                return `${formatPercentage(Number(value) / 100)} i vækst`;
              }
              // For ranges
              else if (/^[0-9.]+ - [0-9.]+$/.test(value)) {
                const [from, to] = value.split(" - ");
                const fromNumber = Number(from);
                const toNumber = Number(to);
                if (isNaN(fromNumber) || isNaN(toNumber)) return null;
                return `${formatNumber(fromNumber)} - ${formatNumber(
                  toNumber,
                )}`;
              }
              // For other numeric values
              else {
                const numberValue = Number(value);
                return isNaN(numberValue) ? null : formatNumber(numberValue);
              }
            })
            .filter((value) => value !== null);
        }

        return {
          filterName: filter.filterName,
          values: formattedValues.join(", "),
        };
      })
      .filter((f) => f.values !== ""); // Filter out empty values
  };

  const getIconByStatus = (status: string) => {
    switch (status) {
      case "New":
        return <Captions size={18} className="text-theme-active" />;
      case "InProgress":
        return <MessageCircleHeart size={18} className="text-theme-active" />;
      case "Postponed":
      case "Scheduled":
        return <AlarmClock size={18} className="text-theme-active" />;
      case "Disqualified":
        return <Ban size={18} className="text-theme-active" />;
      case "Qualified":
      case "ClosedWon":
        return <Handshake size={18} className="text-theme-active" />;
      case "ClosedLost":
        return <ThumbsDown size={18} className="text-theme-active" />;
      default:
        return <OctagonAlert size={18} className="text-theme-active" />;
    }
  };

  const trim = (html: string) => {
    const pattern = "(?:<br\\s*\\/?>|\\s)*";
    html = html.replace(new RegExp("^" + pattern), "");
    html = html.replace(new RegExp(pattern + "$"), "");
    return html;
  };

  const getGrowth = (from: number, to: number) => {
    const diff = to - from;
    let growth = diff / from;
    if (from < 0) growth *= -1;
    return growth;
  };

  // Define key metrics to track
  const metrics = useMemo(
    () =>
      [
        {
          key: "grossResult",
          label: "Bruttofortjeneste",
          format: (val: number, currency: string) =>
            formatReportMoney(currency, val),
        },
        {
          key: "equity",
          label: "Egenkapital",
          format: (val: number, currency: string) =>
            formatReportMoney(currency, val),
        },
        {
          key: "resultBeforeTax",
          label: "Resultat før skat",
          format: (val: number, currency: string) =>
            formatReportMoney(currency, val),
        },
        {
          key: "result",
          label: "Resultat efter skat",
          format: (val: number, currency: string) =>
            formatReportMoney(currency, val),
        },
        {
          key: "ebitda",
          label: "EBITDA",
          format: (val: number, currency: string) =>
            formatReportMoney(currency, val),
        },
        {
          key: "profitFromOrdinaryOperatingActivities",
          label: "Driftsresultat",
          format: (val: number, currency: string) =>
            formatReportMoney(currency, val),
        },
        {
          key: "liquidityRatio",
          label: "Likviditetsgrad",
          format: (val: number) => formatPercentage(val / 100),
        },
        {
          key: "solvencyRatio",
          label: "Soliditetsgrad",
          format: (val: number) => formatPercentage(val / 100),
        },
      ] as const,
    [],
  );

  // Map the company reports with trend data by comparing to the previous one
  const financialReports: FinancialReports<KeyFigureKeys>[] = useMemo(() => {
    if (!companyReports?.length) return [];

    // Sort reports by year (most recent first)
    const sortedReports = [...companyReports]
      .filter((report) => report.reportYear !== null)
      .sort((a, b) => (b.reportYear || 0) - (a.reportYear || 0));

    // Take only the two most recent reports
    const recentReports = sortedReports.slice(0, 2);

    // Find previous year reports for comparison (one year before each recent report)
    return recentReports.map((report, index) => {
      const reportYear = report.reportYear;
      if (!reportYear) return { report, trends: {} };

      const prevYearReport = sortedReports[index + 1];

      const trends: FinancialReports<KeyFigureKeys>["trends"] = {};

      metrics.forEach(({ key }) => {
        const currentValue = report[key];
        const prevValue = prevYearReport?.[key];
        let trend: "up" | "down" | "neutral" = "neutral";

        if (currentValue && prevValue) {
          const growth = getGrowth(prevValue.value, currentValue.value);
          trend = growth > 0 ? "up" : growth < 0 ? "down" : "neutral";
          trends[key] = {
            unit: currentValue.unit,
            value: currentValue.value,
            trend,
          };
        }
      });

      return { report, trends };
    });
  }, [companyReports, metrics]);

  const transitionDirection = useMemo(() => {
    if (leadsIndex === previousLeadsIndex) return null;
    return leadsIndex > previousLeadsIndex ? "right" : "left";
  }, [leadsIndex, previousLeadsIndex]);

  const transitionProps = useMemo<HTMLMotionProps<"div">>(() => {
    switch (transitionDirection) {
      // case "enter":
      //   return {
      //     initial: { opacity: 0 },
      //     animate: {
      //       translateX: "0",
      //       opacity: 1,
      //       transition: { duration: 0.75 },
      //     },
      //   };
      case "left":
        return {
          initial: { translateX: "-100%", translateY: "0" },
          exit: { translateX: "100%", translateY: "0" },
          animate: {
            translateX: "0",
            translateY: "0",
            transition: { duration: 0.5, ease: "circInOut" },
          },
        };
      case "right":
        return {
          initial: { translateX: "100%", translateY: "0" },
          exit: { translateX: "-100%", translateY: "0" },
          animate: {
            translateY: "0",
            translateX: "0",
            transition: { duration: 0.5, ease: "circInOut" },
          },
        };
      default:
        return {
          initial: { translateY: "0", translateX: "0" },
          exit: { translateY: "0", translateX: "0" },
          animate: {
            translateY: "0",
            translateX: "0",
            transition: { duration: 0.5, ease: "circInOut" },
          },
        };
    }
  }, [transitionDirection]);

  const [showTrialCTADialog, setShowTrialCTADialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const { notesOpen } = useLeadNavigation();

  const location = useLocation();
  const navigate = useNavigate();

  const navigateOptions = {
    to: location.pathname,
    search: location.search,
    state: location.state,
  };

  useEffect(() => {
    if (
      preferredContactId &&
      !outOfCreditsShownForContacts.has(preferredContactId) &&
      contactQueriesById.get(preferredContactId)?.error?.name ===
        "paymentRequired"
    ) {
      setOutOfCreditsShownForContacts((prev) =>
        new Set(prev).add(preferredContactId),
      );
      setIsOutOfCreditsModalOpen(true);
    }
  }, [preferredContactId, contactQueriesById, outOfCreditsShownForContacts]);

  return (
    <>
      <motion.div
        id="scroll-headers"
        {...transitionProps}
        className="flex shrink-0 grow flex-col overflow-x-auto p-7"
      >
        <div
          id="scroll-headers"
          className={twMerge(
            "flex shrink-0 grow transition-all duration-300 ease-out",
            notesOpen && "mr-65 overflow-x-auto xl:mr-85",
          )}
        >
          <div
            className={twMerge(
              "mx-auto flex w-full max-w-4xl shrink-0 grow flex-col gap-6 transition-all duration-300 xl:max-w-6xl",
              notesOpen && "min-w-4xl",
            )}
          >
            <div className="flex h-auto w-full flex-col gap-y-0 overflow-hidden rounded-xl">
              <div
                className={twMerge(
                  "flex flex-col bg-black/75 px-14 py-16 text-white backdrop-blur-xl",
                  context.leadView ? "min-h-96" : "min-h-68",
                )}
              >
                {context.leadView && (
                  <>
                    <div className="mb-10 flex w-full items-center justify-between">
                      <div className="text-base font-medium">
                        {simpleCampaign?.name}
                      </div>

                      {hasInsightsAccess(user) && (
                        <div className="flex h-0 items-center">
                          <Button
                            intent="primary"
                            size="lg"
                            animate
                            onPress={() => {
                              window.open(
                                `${lassoUrls.getInsightsUrl()}/${companyLassoId}`,
                                "_blank",
                              );
                            }}
                          >
                            Se i Lasso Insights
                          </Button>
                        </div>
                      )}
                    </div>

                    <Separator theme="dark" className="mb-20 w-full" />
                  </>
                )}

                <div className="flex justify-between gap-x-8">
                  <div className="flex flex-col justify-between gap-y-4">
                    <div>
                      <div className="text-2xl font-medium">
                        {company?.name ?? "-"}
                      </div>
                      <div className="text-dark-theme-on-glass-variant flex flex-nowrap items-center gap-x-1 text-sm">
                        <span className="text-wrap">
                          {company?.address.postalDistrict}
                        </span>
                        {companyWebsite ? (
                          <>
                            &mdash;{" "}
                            <a
                              href={normalizeUrl(companyWebsite).toString()}
                              target="_blank"
                              rel="noreferrer"
                              className="hover:text-theme-active min-w-0 shrink grow-0 truncate overflow-hidden hover:underline"
                            >
                              {cleanWebsiteUrl(companyWebsite)}
                            </a>
                          </>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>

                    <div className="flex">
                      <div className="flex flex-col flex-wrap items-start gap-x-2 gap-y-4">
                        <div>
                          <div className="flex items-center gap-x-2 text-base font-medium text-nowrap">
                            {phoneNumber
                              ? phoneNumber.obfuscated
                                ? formatPhoneNumber(
                                    censorPhoneNumber(phoneNumber.phoneNumber),
                                  )
                                : formatPhoneNumber(phoneNumber.phoneNumber)
                              : "-"}
                            {phoneNumber?.obfuscated && (
                              <PurchaseLiveNumberDialog
                                isTrialPeriod={!!trialPeriod}
                                onPurchase={handlePurchaseLiveNumbers}
                              />
                            )}
                            {!phoneNumber?.obfuscated && phoneNumber && (
                              <Tooltip
                                tooltipText={phoneNumber.explanation}
                                placement="right"
                                tooltipTriggerProps={{ delay: 0 }}
                                className="py-4"
                              >
                                <Focusable>
                                  <ShieldCheck
                                    size={23}
                                    absoluteStrokeWidth
                                    className="text-[#B3EF8B]"
                                  />
                                </Focusable>
                              </Tooltip>
                            )}
                          </div>

                          <div className="text-dark-theme-on-glass-variant text-xs">
                            Live Nummer
                          </div>
                        </div>

                        {phoneNumbers && phoneNumbers.numbers.length >= 1 ? (
                          <RACButton
                            className={twMerge(
                              "w-auto cursor-pointer text-sm font-medium text-nowrap",
                              showPhoneNumbers
                                ? "text-theme-active"
                                : "hover:text-theme-active",
                            )}
                            onPress={() => {
                              setShowPhoneNumbers(!showPhoneNumbers);
                              setShowAllContacts(false);
                            }}
                          >
                            {phoneNumber
                              ? phoneNumbers.numbers.length === 1
                                ? "Vis detaljer"
                                : `Vis detaljer og ${
                                    phoneNumbers.numbers.length - 1
                                  } ${pluralize(
                                    phoneNumbers.numbers.length - 1,
                                    "andet",
                                    "andre",
                                  )}`
                              : `Vis ${phoneNumbers.numbers.length} ${pluralize(
                                  phoneNumbers.numbers.length,
                                  "andet",
                                  "andre",
                                )}`}
                          </RACButton>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {lead?.lead.contact ? (
                    <div className="flex max-w-100 min-w-100 shrink flex-col gap-y-4">
                      <div>
                        {isFetchingPreferredContact ? (
                          <div className="h-8 w-56 animate-pulse rounded-md bg-gray-600"></div>
                        ) : (
                          <div className="text-2xl font-medium">
                            {!preferredContact?.obfuscated
                              ? preferredContact?.name || "-"
                              : preferredContact?.name?.split(" ")[0] || "-"}
                          </div>
                        )}
                        {isFetchingPreferredContact ? (
                          <div className="mt-2 h-5 w-36 animate-pulse rounded-md bg-gray-600"></div>
                        ) : (
                          <div className="text-dark-theme-on-glass-variant text-sm text-wrap">
                            {preferredContact?.titles?.join(", ") ??
                              lead.lead.contact.titles?.join(", ") ??
                              ""}{" "}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap justify-between gap-x-4 gap-y-4">
                        {preferredContactError ? (
                          <div className="text-dark-theme-on-glass-variant text-xs italic">
                            Der opstod problemer med at hente kontaktpersonen
                            fra hjemmesiden.
                            <br />
                            Prøv evt. at finde personen{" "}
                            <a
                              className="cursor-pointer underline"
                              href={
                                lead.lead.contact.paths &&
                                lead.lead.contact.paths[0]
                              }
                              /* rel="noopener noreferrer" is essential for all links that use target="_blank" to open a new tab/window to an external site (like a lead in HubSpot).
                              1. noopener: Prevents a security attack called tabnabbing by blocking the new tab from accessing the original window using window.opener. This protects our platform's security.
                              2. noreferrer:
                              a) Privacy: Prevents our page's URL from being sent to the external site via the HTTP Referer header.
                              b) Security: Acts as a fallback for older browsers that may not fully support noopener.
                            */
                              rel="noopener noreferrer"
                            >
                              her
                            </a>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center gap-x-4">
                              <div className="flex flex-col gap-x-4">
                                {isFetchingPreferredContact ? (
                                  <div className="mt-2 mb-2 h-4 w-36 animate-pulse rounded-md bg-gray-600"></div>
                                ) : preferredContact?.phoneNumbers.length ? (
                                  <span className="text-base font-medium text-nowrap text-white transition-colors">
                                    {formatPhoneNumber(
                                      preferredContact.phoneNumbers[0],
                                    )}
                                  </span>
                                ) : null}

                                {!isFetchingPreferredContact && (
                                  <>
                                    {preferredContact?.emails.length ? (
                                      <div className="text-dark-theme-on-glass-variant text-xs text-nowrap">
                                        {!preferredContact?.obfuscated ? (
                                          <>
                                            <Tooltip tooltipText="Kopier e-mail">
                                              <RACButton
                                                className="hover:text-theme-active inline-block cursor-pointer"
                                                onPress={() => {
                                                  if (
                                                    preferredContact?.emails[0]
                                                  ) {
                                                    navigator.clipboard.writeText(
                                                      preferredContact
                                                        .emails[0],
                                                    );
                                                    toast.success(
                                                      "E-mail kopieret til udklipsholder",
                                                    );
                                                  }
                                                }}
                                              >
                                                {preferredContact.emails[0]}
                                              </RACButton>
                                            </Tooltip>
                                          </>
                                        ) : (
                                          censorEmail(
                                            preferredContact?.emails[0] || "",
                                          )
                                        )}
                                      </div>
                                    ) : null}
                                  </>
                                )}
                              </div>
                              {!isFetchingPreferredContact &&
                                preferredContact?.obfuscated &&
                                preferredContactId && (
                                  <PurchaseContactDialog
                                    contactId={preferredContactId}
                                    isTrialPeriod={!!trialPeriod}
                                    onPurchase={purchaseContact}
                                  />
                                )}
                            </div>
                          </div>
                        )}

                        <div className="w-full">
                          <RACButton
                            className={twMerge(
                              "hover:text-theme-active cursor-pointer text-sm font-medium text-nowrap",
                              showAllContacts && "text-theme-active",
                            )}
                            onPress={() => {
                              setShowAllContacts(!showAllContacts);
                              setShowPhoneNumbers(false);
                            }}
                          >
                            {(() => {
                              const totalContacts = historyContacts.length;
                              const hasPreferred =
                                !!preferredContactId &&
                                historyContacts.some(
                                  (c) => c.id === preferredContactId,
                                );

                              if (totalContacts === 1 && hasPreferred) {
                                return "Vis detaljer";
                              }
                              if (totalContacts === 1 && !hasPreferred) {
                                return "Vis alle kontaktpersoner";
                              }
                              if (totalContacts > 1 && hasPreferred) {
                                return `Vis detaljer og ${totalContacts - 1} ${pluralize(totalContacts - 1, "anden", "andre")}`;
                              }
                              return `Vis ${totalContacts} kontaktpersoner`;
                            })()}
                          </RACButton>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <ContactsTeaser
                      data={historyData}
                      isActive={showAllContacts}
                      onShowContacts={() => {
                        setShowAllContacts(!showAllContacts);
                        setShowPhoneNumbers(false);
                      }}
                    />
                  )}
                </div>
              </div>

              <Card className="relative z-0 overflow-hidden rounded-none">
                <AnimateChangeInHeight>
                  <AnimatePresence mode="popLayout">
                    {showAllContacts ? (
                      <motion.div
                        key="all-contacts"
                        className="relative z-1"
                        initial={{ opacity: 0, y: "-100%" }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: "-100%" }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                      >
                        <AllContacts
                          handleCopy={handleCopy}
                          loadingIds={loadingContactIds}
                          contacts={loadedContacts}
                          historyContacts={historyContacts}
                          preferredContact={preferredContact}
                          preferedContactId={preferredContactId}
                          onClose={() => setShowAllContacts(false)}
                          lead={lead}
                          trialPeriod={!!trialPeriod}
                          setShowTrialCTADialog={setShowTrialCTADialog}
                          setContactsToLoad={setContactIdsToLoad}
                          purchaseContact={purchaseContact}
                          contactQueriesById={contactQueriesById}
                          onOutOfCredits={(contactId) => {
                            setOutOfCreditsShownForContacts((prev) =>
                              new Set(prev).add(contactId),
                            );
                            setIsOutOfCreditsModalOpen(true);
                          }}
                          outOfCreditsShownForContacts={
                            outOfCreditsShownForContacts
                          }
                          onRefreshContact={refreshContact}
                          refreshingContactIds={refreshingContactIds}
                        />
                      </motion.div>
                    ) : showPhoneNumbers ? (
                      <motion.div
                        key="phone-numbers"
                        className="relative z-1"
                        initial={{ opacity: 0, y: "-100%" }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: "-100%" }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                      >
                        <LiveNumbers
                          handleCopy={handleCopy}
                          phoneNumbers={phoneNumbers}
                          onClose={() => setShowPhoneNumbers(false)}
                          setShowTrialCTADialog={setShowTrialCTADialog}
                          onPurchaseLiveNumbers={handlePurchaseLiveNumbers}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="default"
                        className="relative z-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                      >
                        {context.leadView ? (
                          <div className="flex">
                            {lead?.lead.events &&
                              lead.lead.events.length > 0 && (
                                <div className="flex py-16">
                                  <div className="h-auto min-w-28 px-14">
                                    <Heading className="text-xs uppercase">
                                      Salgstrigger
                                    </Heading>
                                    <Separator className="my-12" />
                                    <CheckCircle
                                      size={32}
                                      className="text-theme-active"
                                    />
                                    <ul className="mt-5 flex flex-col gap-y-4">
                                      {lead.lead.events.map((event, index) => (
                                        <li
                                          key={index}
                                          className="flex flex-col justify-center gap-x-2 gap-y-5 text-xs font-medium"
                                        >
                                          <div className="flex flex-col gap-y-1">
                                            <div className="text-base text-nowrap">
                                              {
                                                formattedEventTriggers[event]
                                                  .label
                                              }
                                            </div>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  <Separator
                                    className="mx-14"
                                    orientation="vertical"
                                  />
                                </div>
                              )}
                            <div className="w-full shrink grow-0 overflow-hidden pt-15">
                              <Heading className="px-14 text-xs uppercase">
                                <div
                                  className={twMerge(
                                    "bg-linear-to-r bg-clip-text font-medium",
                                    campaignConfiguration?.name &&
                                      leadTypeColors[
                                        campaignConfiguration.name as keyof typeof leadTypeColors
                                      ],
                                  )}
                                >
                                  {campaignConfiguration?.name} lead
                                </div>
                              </Heading>

                              <div className="px-14">
                                <Separator className="my-12" />
                              </div>

                              <div
                                className="flex w-full items-stretch overflow-hidden transition-[mask-image] duration-200"
                                style={{
                                  maskImage: areFiltersScrolled
                                    ? "linear-gradient(to right, transparent 0%, white 15%)"
                                    : "linear-gradient(to left, transparent 0%, white 15%)",
                                }}
                              >
                                <div
                                  className="flex min-h-40 w-full overflow-x-auto"
                                  onScroll={(e) => {
                                    setAreFiltersScrolled(
                                      e.currentTarget.scrollLeft > 0,
                                    );
                                  }}
                                >
                                  {filterNames.length > 0 ? (
                                    filterNames?.map((filterName, index) => (
                                      <React.Fragment key={index}>
                                        <div className="flex min-w-75 shrink grow-0 flex-col gap-y-5 px-14">
                                          {(() => {
                                            const Icon =
                                              filterCategories[
                                                filterName.categoryKey
                                              ].icon;
                                            if (!Icon) return null;
                                            return (
                                              <Icon
                                                size={32}
                                                className="text-theme-active"
                                              />
                                            );
                                          })()}
                                          {filterName.filterGroups.map(
                                            (filterGroup, index) => {
                                              return (
                                                <div
                                                  key={index + "filterGroup"}
                                                  className="flex flex-col gap-y-1"
                                                >
                                                  <Heading
                                                    level={4}
                                                    className="text-base"
                                                  >
                                                    {
                                                      filterGroup.filterGroup
                                                        .title
                                                    }
                                                  </Heading>
                                                  <div className="text-theme-on-background-variant text-xs">
                                                    {formatFilterValues(
                                                      filterGroup.filters,
                                                    )
                                                      .flatMap((f) => f.values)
                                                      .join(", ")}
                                                  </div>
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                        {index !== filterNames.length - 1 && (
                                          <div className="flex items-stretch">
                                            <Separator orientation="vertical" />
                                          </div>
                                        )}
                                      </React.Fragment>
                                    ))
                                  ) : (
                                    <Heading
                                      level={4}
                                      className="px-14 text-base"
                                    >
                                      {isTargetGroupPending ||
                                      isTargetGroupFetching
                                        ? "Henter målgruppe..."
                                        : isTargetGroupError
                                          ? "Der opstod en fejl ved indlæsning af målgruppe"
                                          : "Ingen målgruppe defineret"}
                                    </Heading>
                                  )}
                                </div>
                              </div>
                              <div className="my-6 flex w-full cursor-pointer justify-end px-14 text-sm">
                                <MenuTrigger
                                  isOpen={openDialog}
                                  onOpenChange={setOpenDialog}
                                >
                                  <RACButton
                                    className={twMerge(
                                      "hover:text-theme-active text-theme-on-background-variant cursor-pointer text-xs transition-colors",
                                      openDialog && "text-theme-active",
                                    )}
                                  >
                                    Problemer med leadet?
                                  </RACButton>
                                  {lead && (
                                    <ReportErrorDialog
                                      modalType="error"
                                      isOpen={openDialog}
                                      onOpenChange={setOpenDialog}
                                      lead={lead.lead}
                                      popoverProps={{
                                        showArrow: true,
                                        placement: "left",
                                        offset: 20,
                                      }}
                                    />
                                  )}
                                </MenuTrigger>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </AnimateChangeInHeight>
              </Card>
            </div>

            {/* Main content area with columns */}
            <div className="flex w-full flex-col gap-6 lg:flex-row">
              {/* Left column - smaller cards */}
              <div className="flex flex-col gap-6 lg:w-1/3">
                {context.leadView && (
                  <Card className="px-14 py-17.5">
                    <Heading level={4} className="text-xs uppercase">
                      Aktivitet
                    </Heading>
                    <Separator className="my-12 w-full" />
                    <div>
                      <ul className="flex list-none flex-col gap-y-8">
                        {[...(lead?.history ?? [])].reverse().map((history) => {
                          return (
                            <li key={history.id}>
                              <div className="text-xs">
                                {getIconByStatus(history.status)}
                                <div className="my-2 font-bold">
                                  {history.statusReason
                                    ? history.statusReason +
                                      (history.followUpTime
                                        ? ` d. ${formatDateTime(
                                            new Date(history.followUpTime),
                                            {
                                              hour: "numeric",
                                              minute: "numeric",
                                              day: "2-digit",
                                              month: "2-digit",
                                            },
                                          )}`
                                        : "")
                                    : getLeadStatusLabel(history.status)}
                                </div>
                                <div className="">
                                  d. {formatDate(history.timestamp)}
                                </div>
                                {history.status !== "New" && (
                                  <div>
                                    af{" "}
                                    {usersFlattened?.find(
                                      (u) => u.id === history.changedById,
                                    )?.name ?? "System"}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </Card>
                )}

                <Card className="px-14 py-17.5">
                  <Heading level={4} className="text-xs uppercase">
                    Relationer
                  </Heading>
                  <Separator className="my-12 w-full" />
                  <div>
                    <Relations
                      board={company?.board}
                      management={company?.management}
                      ownership={company?.ownership}
                      stakeholders={company?.stakeholders}
                    />
                  </div>
                </Card>

                {observations &&
                  observations.observations.filter((o) => o.isObserved).length >
                    0 && (
                    <Card className="px-14 py-17.5">
                      <Heading level={4} className="text-xs uppercase">
                        Præstationsevne
                      </Heading>
                      <Separator className="my-12 w-full" />
                      <span className="text-xs">Denne virksomhed er</span>
                      <ul className="mt-6 flex flex-col gap-y-4">
                        {observations?.observations
                          .filter((o) => o.isObserved)
                          .map((observation) => (
                            <Tooltip
                              key={observation.type}
                              tooltipText={observation.observationDescription!}
                              tooltipTriggerProps={{ delay: 0 }}
                              className="flex flex-col"
                            >
                              <FocusableListItem
                                key={observation.type}
                                className="flex items-center gap-x-2 text-xs font-bold"
                              >
                                <CircleCheckBig
                                  size={18}
                                  className="text-theme-active"
                                />
                                <span>{observation.title}</span>
                              </FocusableListItem>
                            </Tooltip>
                          ))}
                      </ul>
                    </Card>
                  )}
              </div>

              {/* Right column - larger cards */}
              <div className="flex min-w-0 shrink grow-0 flex-col gap-6 lg:w-2/3">
                <Card className="py-17.5">
                  <div className="px-14">
                    <Heading
                      level={4}
                      className="text-theme-on-background text-xs uppercase"
                    >
                      Stamoplysninger
                    </Heading>
                    <Separator className="my-12 w-full" />
                  </div>
                  <table className="w-full text-left">
                    <tbody className="*:odd:bg-theme-on-background/3 text-xs">
                      <tr className="*:px-14 *:py-3">
                        <th className="text-nowrap">Virksomhedens alder</th>
                        <td>
                          {company?.creationDate
                            ? formatSince(company.creationDate)
                            : "-"}
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>CVR</th>
                        <td>{company?.cvr ?? "-"}</td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Adresse</th>
                        <td>
                          {company?.address
                            ? formatAddress(company.address)
                            : "-"}
                          {company?.address && (
                            <Tooltip tooltipText="Vis i Google Maps">
                              <Focusable>
                                <a
                                  href={
                                    "https://www.google.com/maps/search/?api=1&query=" +
                                    encodeURIComponent(
                                      formatAddress(company.address),
                                    )
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                  className="hover:text-theme-active -my-px ml-2 inline-block align-top hover:underline"
                                >
                                  <MapPin size={18} />
                                </a>
                              </Focusable>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Antal ansatte</th>
                        <td>
                          {company?.employees?.count
                            ? `${company.employees.count} ansatte`
                            : "-"}
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Branche</th>
                        <td className="line-clamp-2">
                          {company?.industry.text ?? "-"}
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Formål</th>
                        <td>
                          <FocusableText
                            className="line-clamp-3"
                            text={company?.purpose ?? "-"}
                          />
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Telefonnummer</th>
                        <td>
                          {company?.phone ? (
                            <span className="group/row flex items-center">
                              {company.phone}
                              <span className="ml-2 flex items-center gap-x-2 opacity-0 transition-opacity group-hover/row:opacity-100">
                                <RACButton
                                  className="hover:text-theme-active cursor-pointer transition-colors"
                                  onPress={() => handleCopy(company.phone!)}
                                >
                                  <Copy size={16} />
                                </RACButton>

                                <a
                                  href={`tel:${company.phone}`}
                                  className="hover:text-theme-active cursor-pointer transition-colors"
                                >
                                  <Phone size={16} />
                                </a>
                              </span>
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Reklamebeskyttet</th>
                        <td>{company?.commerciallyProtected ? "Ja" : "Nej"}</td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>Website</th>
                        <td>
                          {companyWebsite ? (
                            <>
                              <a
                                href={normalizeUrl(companyWebsite).toString()}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-theme-active min-w-0 shrink grow-0 truncate overflow-hidden hover:underline"
                              >
                                {cleanWebsiteUrl(companyWebsite)}
                              </a>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="*:px-14 *:py-3">
                        <th>E-mail</th>
                        <td>
                          {company?.email ? (
                            <>
                              <a
                                href={`mailto:${company.email}`}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-theme-active min-w-0 shrink grow-0 truncate overflow-hidden hover:underline"
                              >
                                {company.email}
                              </a>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card>

                <Card className="px-14 py-17.5">
                  <Heading level={4} className="text-xs uppercase">
                    Firmabeskrivelse
                  </Heading>
                  <Separator className="my-12 w-full" />
                  {!isResumeFetching &&
                  resumeFetched &&
                  !companyResume?.content ? (
                    isResumeError ? (
                      <div className="text-xs font-medium">
                        Der opstod en fejl ved hentning af erhvervsresume.{" "}
                        <RACButton
                          className="text-theme-active cursor-pointer hover:underline"
                          onPress={() => refetchResume()}
                        >
                          Prøv igen
                        </RACButton>
                      </div>
                    ) : (
                      <div className="text-xs font-medium">
                        Intet erhvervsresume for denne virksomhed.
                      </div>
                    )
                  ) : (
                    <div
                      ref={resumeContentRef}
                      style={{
                        maskImage:
                          !expandResume && needsTruncation
                            ? "linear-gradient(to bottom, white, transparent)"
                            : "none",
                        height:
                          !expandResume && needsTruncation
                            ? `${truncatedHeight}px`
                            : "max-content",
                      }}
                      className="text-theme-on-background relative mb-2 overflow-y-clip text-xs leading-[18px] font-medium transition-[height] duration-200"
                    >
                      {!resumeFetched || !companyResume ? (
                        "Henter resume..."
                      ) : (
                        <>
                          <TemplateText
                            template={trim(companyResume.content)}
                            makeLinks={false}
                          />
                        </>
                      )}
                    </div>
                  )}
                  {!expandResume &&
                    resumeFetched &&
                    companyResume?.content &&
                    needsTruncation && (
                      <a
                        className="text-theme-active inline cursor-pointer text-xs select-none"
                        onClick={() => setExpandResume(!expandResume)}
                      >
                        Læs mere
                      </a>
                    )}
                </Card>
                {financialReports.length > 0 && (
                  <Card className="grow-0 py-17.5">
                    <div className="px-14">
                      <div className="flex items-center justify-between">
                        <Heading
                          level={4}
                          className="text-theme-on-background text-xs uppercase"
                        >
                          Økonomioplysninger
                        </Heading>
                        {financialReports.length > 0 && (
                          <Button
                            intent="primary"
                            size="sm"
                            onPress={() => {
                              window.open(
                                financialReports[0].report.reportUrl,
                                "_blank",
                              );
                            }}
                          >
                            <div className="flex items-center gap-x-2">
                              Regnskab{" "}
                              {`${financialReports[0].report.reportYear}`}
                              <Download size={11} />
                            </div>
                          </Button>
                        )}
                      </div>
                      <Separator className="my-12 w-full" />
                    </div>
                    <div id="scroll-headers" className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className={`*:px-14 *:py-3`}>
                            <th className="w-0"></th>
                            {financialReports.map(({ report }) => (
                              <th
                                key={report.reportYear}
                                className="py-3 text-right font-bold"
                              >
                                {report.reportYear}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="*:odd:bg-black/3 dark:*:odd:bg-white/3">
                          {metrics?.map(({ key, label, format }) => {
                            const metricValues = [
                              financialReports[0]?.trends?.[key],
                              financialReports[1]?.trends?.[key],
                            ];

                            return (
                              <tr key={key} className="*:px-14 *:py-3">
                                <th className="w-0 text-nowrap">{label}</th>
                                {metricValues.map((metric, index) => (
                                  <td key={index}>
                                    <div className="flex items-center justify-end gap-2 text-nowrap">
                                      <span className="font-medium">
                                        {metric
                                          ? format(metric.value, metric.unit)
                                          : "-"}
                                      </span>
                                      {metric && metric.trend !== "neutral" && (
                                        <span
                                          className={`-mr-6 ${
                                            metric.trend === "up"
                                              ? "text-green-500"
                                              : "text-red-500"
                                          }`}
                                        >
                                          {metric.trend === "up" ? (
                                            <TrendingUp className="size-4" />
                                          ) : (
                                            <TrendingDown className="size-4" />
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <AlertModal
        isOpen={isOutOfCreditsModalOpen}
        iconDisable={true}
        title="Du er løbet tør for credits"
        description="Du kan desværre ikke vise denne kontakt, da du er løbet tør for credits. Tilkøb flere credits, eller vent til næste abonnementsperiode."
        onClose={() => {
          setIsOutOfCreditsModalOpen(false);
        }}
        buttonChildren={
          <div className="flex w-full gap-5">
            <Button
              animate
              intent="secondary"
              onPress={() => setIsOutOfCreditsModalOpen(false)}
            >
              Luk
            </Button>

            {isAdmin(user) && (
              <Button
                animate
                onPress={() => {
                  setIsOutOfCreditsModalOpen(false);
                  navigate({
                    to: "/buy-credits",
                    state: {
                      routeStates: {
                        "/_dashboard/buy-credits": {
                          reason: "notEnoughCredits",
                          closeNavigateOptions: navigateOptions,
                          successNavigateOptions: navigateOptions,
                        },
                      },
                    },
                  });
                }}
              >
                Tilkøb credits
              </Button>
            )}
          </div>
        }
      />

      <TrialCTAModal
        isOpen={showTrialCTADialog}
        onOpenChange={setShowTrialCTADialog}
        title={trialText}
      />
    </>
  );
}

function ContactsTeaser({
  data,
  onShowContacts,
  isActive,
}: {
  data?: HistoryResponse;
  onShowContacts?: () => void;
  isActive: boolean;
}) {
  const contactsFound = useMemo(() => {
    if (!data) return 0;
    const filtered = data
      .flatMap((h) => h.contacts)
      .filter((c) => c.titles.length);

    return filtered.length;
  }, [data]);
  const contacts = useMemo(() => {
    if (!data) return [];
    return data?.flatMap((h) => h.contacts).filter((c) => c.titles.length);
  }, [data]);

  const uniqueTitles = useMemo(
    () => [
      ...new Set(
        contacts.map((c) => c.titles[0]).filter(Boolean), // Filter out falsy values
      ),
    ],
    [contacts],
  );

  const MAX_AVATARS = 4;
  const remaining = contactsFound - MAX_AVATARS;
  const visibleTitles = uniqueTitles.slice(0, 2);
  const remainingTitles = uniqueTitles.length - visibleTitles.length;

  if (!data || contactsFound === 0) return null;

  return (
    <div className="flex max-w-100 min-w-100 shrink flex-col items-start justify-between gap-y-7">
      {/* Avatar row */}
      <div className="flex -space-x-2 text-xs font-semibold">
        {contacts.slice(0, MAX_AVATARS).map((c, i) => (
          <span
            key={c.id ?? i}
            className="bg-theme-primary/20 border-theme-primary/10 text-theme-active inline-flex size-10 items-center justify-center rounded-full border"
          >
            {abbreviate(c.titles[0])}
          </span>
        ))}
        {remaining > 0 && (
          <span className="z-10 inline-flex size-10 items-center justify-center rounded-full border border-white/10 bg-white/20 text-white">
            +{remaining}
          </span>
        )}
      </div>

      <div className="flex flex-col items-start justify-start gap-y-4">
        <div className="flex flex-col">
          {/* Heading */}
          <div className="text-base font-medium">
            {contactsFound}{" "}
            {pluralize(contactsFound, "kontaktperson", "kontaktpersoner")}{" "}
            fundet
          </div>

          {/* Subtitle with role names */}
          {uniqueTitles.length > 0 && (
            <div className="text-dark-theme-on-glass-variant text-xs">
              {visibleTitles.join(", ")}
              {remainingTitles > 0 && ` og ${remainingTitles} andre`}
            </div>
          )}
        </div>

        {/* CTA link */}
        {onShowContacts && (
          <RACButton
            className={twMerge(
              "hover:text-theme-active cursor-pointer text-sm font-medium text-white",
              isActive && "text-theme-active",
            )}
            onPress={onShowContacts}
          >
            Vis {pluralize(contactsFound, "kontaktperson", "kontaktpersoner")}
          </RACButton>
        )}
      </div>
    </div>
  );
}

function FocusableListItem(
  props: { children: React.ReactNode } & React.HTMLAttributes<HTMLLIElement>,
) {
  const ref = useRef<HTMLLIElement>(null);

  const { focusableProps } = useFocusable(props, ref);

  return (
    <li ref={ref} tabIndex={0} className={props.className} {...focusableProps}>
      {props.children}
    </li>
  );
}

function AllContacts({
  contacts,
  onClose,
  lead,
  trialPeriod,
  loadingIds,
  historyContacts,
  handleCopy,
  setShowTrialCTADialog,
  setContactsToLoad,
  purchaseContact,
  preferedContactId,
  contactQueriesById,
  onOutOfCredits,
  outOfCreditsShownForContacts,
  onRefreshContact,
  refreshingContactIds,
}: {
  contacts: ContactResponse[];
  preferredContact: ContactResponse | null | undefined;
  onClose?: () => void;
  lead: LeadResponse | undefined;
  trialPeriod: boolean;
  loadingIds: Set<string>;
  historyContacts: HistoryContact[];
  handleCopy: (text: string) => void;
  setShowTrialCTADialog: (value: boolean) => void;
  setContactsToLoad: React.Dispatch<React.SetStateAction<Set<string>>>;
  purchaseContact: (contactId: string) => void;
  preferedContactId: string | null | undefined;
  contactQueriesById: Map<
    string,
    { error?: { name?: string } | null; data?: ContactResponse | null }
  >;
  onOutOfCredits: (contactId: string) => void;
  outOfCreditsShownForContacts: Set<string>;
  onRefreshContact: (contactId: string) => Promise<void>;
  refreshingContactIds: Set<string>;
}) {
  const { currentTheme } = useContext(ThemeContext);

  const contactGroupOrder = useMemo(
    () =>
      new Map(
        [
          "Founders",
          "Direktion",
          "Sekretariat",
          "Ledelse",
          "Salg",
          "Marketing",
          "Økonomi",
          "Kommunikation",
          "IT",
          "IT Udvikling",
          "Personale",
          "Rådgivere",
          "Revisor",
          "Forretningsudvikling",
          "Advokater",
          "Jurister",
          "Administration",
          "Partnere",
          "Logistik",
          "Konsulenter",
          "Redaktion",
          "Elever",
          "Service",
          "Kvalitet",
        ].map((group, i) => [group, i] as const),
      ),
    [],
  );

  const contactTitleOrder = useMemo(
    () =>
      new Map(
        [
          "adm. dir",
          "administrerende direktør",
          "cco",
          "ceo",
          "cfo",
          "chief",
          "cio",
          "cmo",
          "coo",
          "cso",
          "cto",
          "cro",
          "director",
          "direktion",
          "editor-in-chief",
          "president",
          "direktør",
          "stifter",
          "founder",
          "co-founder",
          "grundlægger",
          "ejer",
          "medejer",
          "indehaver",
        ].map((title, i) => [title, i] as const),
      ),
    [],
  );

  const loadedContactsMap = useMemo(() => {
    const map = new Map<string, ContactResponse>();
    contacts.forEach((contact) => {
      if (contact.id !== null) {
        map.set(contact.id, contact);
      }
    });
    return map;
  }, [contacts]);

  const contactsByDepartmentFromHistory = useMemo(() => {
    const grouped = new Map<string, HistoryContact[]>();

    historyContacts.forEach((historyContact) => {
      const department = historyContact.group || "Andre";

      let departmentContacts = grouped.get(department);
      if (!departmentContacts) {
        departmentContacts = [];
        grouped.set(department, departmentContacts);
      }
      departmentContacts.push(historyContact);
    });

    return new Map(
      [...grouped].sort(([aName], [bName]) => {
        if (aName === "Andre") return 1;
        if (bName === "Andre") return -1;
        const a = contactGroupOrder.get(aName);
        const b = contactGroupOrder.get(bName);
        if (a === undefined && b === undefined) return 0;
        if (a === undefined) return 1;
        if (b === undefined) return -1;
        return a - b;
      }),
    );
  }, [historyContacts, contactGroupOrder]);

  const contactsByDepartmentAndTitleFromHistory = useMemo(() => {
    const result = new Map<string, Map<string, HistoryContact[]>>();

    contactsByDepartmentFromHistory.forEach((contacts, department) => {
      const titleMap = new Map<string, HistoryContact[]>();

      contacts.forEach((contact) => {
        if (!contact.titles || contact.titles.length === 0) {
          return;
        }
        const titleKey = contact.titles[0];
        if (!titleKey) return;

        let title = titleMap.get(titleKey);
        if (!title) {
          title = [];
          titleMap.set(titleKey, title);
        }
        title.push(contact);
      });

      const sortedTitles = new Map(
        [...titleMap].sort(([aTitle], [bTitle]) => {
          const aLower = aTitle.toLowerCase();
          const bLower = bTitle.toLowerCase();
          const a = contactTitleOrder.get(aLower);
          const b = contactTitleOrder.get(bLower);
          if (a === undefined && b === undefined)
            return aLower.localeCompare(bLower, "da");
          if (a === undefined) return 1;
          if (b === undefined) return -1;
          return a - b;
        }),
      );

      if (sortedTitles.size === 0) {
        return;
      }

      result.set(department, sortedTitles);
    });

    return result;
  }, [contactsByDepartmentFromHistory, contactTitleOrder]);

  const handleContactSelect = (historyContact: HistoryContact) => {
    setSelectedContactId(historyContact.id);
    setContactsToLoad((prev) => new Set([...prev, historyContact.id]));
  };

  const [selectedContactId, setSelectedContactId] = useState(
    preferedContactId || null,
  );

  const selectedContact = useMemo(() => {
    if (!selectedContactId) return null;
    return loadedContactsMap.get(selectedContactId) ?? null;
  }, [loadedContactsMap, selectedContactId]);

  const [openDialog, setOpenDialog] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedContactId) return;
    if (outOfCreditsShownForContacts.has(selectedContactId)) return;
    const query = contactQueriesById.get(selectedContactId);
    if (query?.error?.name === "paymentRequired") {
      onOutOfCredits(selectedContactId);
    }
  }, [
    selectedContactId,
    contactQueriesById,
    onOutOfCredits,
    outOfCreditsShownForContacts,
  ]);

  useEffect(() => {
    if (!preferedContactId) return;

    for (const [
      department,
      titleMap,
    ] of contactsByDepartmentAndTitleFromHistory.entries()) {
      for (const [title, historyContactsList] of titleMap.entries()) {
        if (
          historyContactsList.length > 1 &&
          historyContactsList.some((c) => c.id === preferedContactId)
        ) {
          const key = department + title;
          setExpanded((prev) => new Set(prev).add(key));
          return;
        }
      }
    }
  }, [preferedContactId, contactsByDepartmentAndTitleFromHistory]);

  const isContactSelected = (contactId: string) =>
    selectedContactId === contactId;

  const { isDesktop } = useScreenSize();

  const contactRef = useRef<HTMLDivElement>(null);

  const { isAtBottom, canScroll } = useScrollAtBottom(contactRef.current);

  return (
    <div className="flex h-160 w-full flex-col gap-y-6 overflow-hidden px-14 py-16">
      <div className="flex h-4 w-full items-center justify-between">
        <Heading className="text-xs uppercase">Alle kontakter</Heading>

        <RACButton
          className="cursor-pointer"
          onPress={() => {
            onClose?.();
          }}
        >
          <X />
        </RACButton>
      </div>
      <Separator className="my-6 w-full" />

      <div className="flex h-0 w-full grow">
        <div className="relative flex h-full w-2/6 shrink grow-0 flex-col">
          <div
            ref={contactRef}
            className="relative inset-0 -my-3 -mr-14 flex flex-col overflow-y-auto pr-14"
          >
            {[...contactsByDepartmentAndTitleFromHistory.entries()].map(
              ([department, titleMap]) => {
                return (
                  <div key={department} className="mb-6 flex flex-col">
                    <div className="py-2 text-sm font-bold">{department}</div>

                    <div className="flex flex-col">
                      {[...titleMap.entries()].map(
                        ([title, historyContactsList]) => {
                          if (historyContactsList.length === 1) {
                            const historyContact = historyContactsList[0];
                            return (
                              <RACButton
                                key={historyContact.id}
                                className={twMerge(
                                  "group/contact flex shrink-0 cursor-pointer items-start gap-y-1 rounded-md py-2 text-left",
                                  isContactSelected(historyContact.id) &&
                                    "text-theme-active",
                                )}
                                onPress={() =>
                                  handleContactSelect(historyContact)
                                }
                              >
                                <div className="group-hover/contact:text-theme-active flex w-full justify-between text-sm transition-colors">
                                  <span>{title}</span>
                                  {
                                    <div className="flex items-center gap-2">
                                      {historyContact.phoneNumbers > 0 && (
                                        <Phone size={14} />
                                      )}
                                      {historyContact.emails > 0 && (
                                        <Mail size={14} />
                                      )}
                                    </div>
                                  }
                                </div>
                              </RACButton>
                            );
                          }

                          const isExpanded = expanded.has(department + title);
                          const key = department + title;

                          return (
                            <div key={title} className="w-full">
                              <RACButton
                                className={twMerge(
                                  "hover:text-theme-active flex w-full cursor-pointer items-center justify-between border-b border-transparent py-2 text-sm transition-colors",
                                  isExpanded && "border-theme-outline border-b",
                                )}
                                onPress={() => {
                                  setExpanded((prev) => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(key)) {
                                      newSet.delete(key);
                                    } else {
                                      newSet.add(key);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                {`${title} (${historyContactsList.length})`}
                                <motion.div
                                  animate={{ rotate: isExpanded ? 180 : 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <ChevronDown size={18} />
                                </motion.div>
                              </RACButton>
                              <motion.div
                                initial={false}
                                animate={{
                                  height: isExpanded ? "auto" : 0,
                                  opacity: isExpanded ? 1 : 0,
                                }}
                                transition={{ duration: 0.3 }}
                                style={{ overflow: "hidden" }}
                              >
                                <div className="flex flex-col p-2 pr-0 pl-3">
                                  {historyContactsList.map(
                                    (historyContact, index) => (
                                      <motion.div
                                        key={historyContact.id}
                                        initial={false}
                                        animate={{
                                          opacity: isExpanded ? 1 : 0,
                                          x: isExpanded ? 0 : -10,
                                        }}
                                        transition={{
                                          duration: 0.2,
                                          delay: isExpanded
                                            ? index * 0.1
                                            : historyContactsList.length * 0.1 -
                                              index * 0.1,
                                        }}
                                      >
                                        <RACButton
                                          className={twMerge(
                                            "group/contact flex w-full shrink-0 cursor-pointer items-start rounded-md py-1 text-left",
                                            isContactSelected(
                                              historyContact.id,
                                            ) && "text-theme-active",
                                          )}
                                          onPress={() =>
                                            handleContactSelect(historyContact)
                                          }
                                        >
                                          <div className="group-hover/contact:text-theme-active flex w-full justify-between text-sm transition-colors">
                                            {title} {index + 1}
                                            <div className="flex items-center gap-2">
                                              {historyContact.phoneNumbers >
                                                0 && <Phone size={14} />}
                                              {historyContact.emails > 0 && (
                                                <Mail size={14} />
                                              )}
                                            </div>
                                          </div>
                                        </RACButton>
                                      </motion.div>
                                    ),
                                  )}
                                </div>
                              </motion.div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
          <div
            className={twMerge(
              `from-theme-background/10 to-theme-background pointer-events-none absolute -bottom-4 flex h-25 min-h-25 w-full items-center gap-1 bg-linear-to-b transition-opacity duration-300`,
              canScroll && !isAtBottom ? "opacity-100" : "opacity-0",
            )}
          />
        </div>

        <Separator orientation="vertical" className="mx-14" />

        <AnimatePresence>
          <motion.div className="flex w-0 grow flex-col">
            {!selectedContactId ? (
              <div className="text-theme-on-background-variant flex h-full w-full items-center justify-center text-sm">
                Vælg en kontakt
              </div>
            ) : loadingIds.has(selectedContactId) ? (
              <motion.div
                key={selectedContactId}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="flex h-full w-full items-center justify-center"
              >
                <div className="border-theme-active h-12 w-12 animate-spin rounded-full border-4 border-t-4 border-r-4 border-l-4 border-t-transparent" />
              </motion.div>
            ) : !loadedContactsMap.get(selectedContactId)?.name ? (
              <motion.div
                key={selectedContactId}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="flex h-full w-full items-center justify-center"
              >
                <div className="text-theme-on-glass-variant px-4 text-xs italic">
                  Der opstod problemer med at hente kontaktpersonen fra
                  hjemmesiden.
                  {historyContacts.find((h) => h.id === selectedContactId)
                    ?.paths?.[0] && (
                    <>
                      <br /> Prøv evt. at finde personen{" "}
                      <a
                        className="cursor-pointer underline"
                        href={
                          historyContacts.find(
                            (h) => h.id === selectedContactId,
                          )?.paths[0]
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        her
                      </a>
                    </>
                  )}
                </div>
              </motion.div>
            ) : (
              selectedContact && (
                <motion.div
                  key={selectedContactId}
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex flex-col gap-3">
                      <div className="text-base font-bold">
                        {!selectedContact.obfuscated
                          ? selectedContact.name || "-"
                          : selectedContact.name?.split(" ")[0] || "-"}
                      </div>
                      <div className="max-w-sm text-sm">
                        {selectedContact.titles[0] || "-"}
                      </div>
                    </div>

                    {lead && !selectedContact.obfuscated && (
                      <MenuTrigger
                        isOpen={openDialog}
                        onOpenChange={setOpenDialog}
                      >
                        <RACButton
                          className={twMerge(
                            "hover:text-theme-active cursor-pointer text-xs transition-colors",
                            openDialog && "text-theme-active",
                          )}
                        >
                          Rapporter kontakt
                        </RACButton>
                        <ReportErrorDialog
                          modalType="single_phone"
                          selectedPhoneNumber={undefined}
                          lead={lead.lead}
                          isOpen={openDialog}
                          onOpenChange={setOpenDialog}
                          popoverProps={{
                            showArrow: true,
                            placement: "left",
                            offset: 20,
                            containerPadding: 10,
                          }}
                        />
                      </MenuTrigger>
                    )}
                  </div>

                  <div className="flex flex-col gap-y-4">
                    <>
                      {selectedContact.phoneNumbers?.length > 0 &&
                        (!selectedContact.obfuscated ? (
                          <div className="flex items-center">
                            <a
                              className={twMerge(
                                "text-sm font-medium transition-colors",
                                !isDesktop && "hover:text-theme-active",
                              )}
                              {...(!isDesktop
                                ? trialPeriod
                                  ? {
                                      onClick: () =>
                                        setShowTrialCTADialog(true),
                                    }
                                  : {
                                      href: `tel:${selectedContact.phoneNumbers?.[0]}`,
                                    }
                                : {})}
                            >
                              {formatPhoneNumber(
                                selectedContact.phoneNumbers?.[0],
                              )}
                            </a>
                            <div className="mr-auto ml-2 flex items-center gap-x-2">
                              <Tooltip tooltipText="Kopier telefonnummer">
                                <RACButton
                                  className="hover:text-theme-active cursor-pointer transition-colors"
                                  onPress={() => {
                                    if (trialPeriod) {
                                      setShowTrialCTADialog(true);
                                      return;
                                    }

                                    if (selectedContact.phoneNumbers?.[0])
                                      handleCopy(
                                        selectedContact.phoneNumbers?.[0],
                                      );
                                  }}
                                >
                                  <Copy size={16} />
                                </RACButton>
                              </Tooltip>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">
                              {formatPhoneNumber(
                                selectedContact.phoneNumbers?.[0],
                              )}{" "}
                            </span>
                          </div>
                        ))}
                    </>
                    {selectedContact.emails?.length > 0 && (
                      <div>
                        <span className="text-sm">
                          {!selectedContact.obfuscated
                            ? selectedContact.emails?.[0]
                            : censorEmail(selectedContact.emails?.[0] || "")}
                        </span>
                        {!selectedContact.obfuscated && (
                          <Tooltip tooltipText="Kopier e-mail">
                            <RACButton
                              className="hover:text-theme-active cursor-pointer text-sm"
                              onPress={() => {
                                if (selectedContact.emails?.[0]) {
                                  navigator.clipboard.writeText(
                                    selectedContact.emails[0],
                                  );
                                  toast.success(
                                    "E-mail kopieret til udklipsholder",
                                  );
                                }
                              }}
                            >
                              <Copy size={16} className="ml-2 inline-block" />
                            </RACButton>
                          </Tooltip>
                        )}
                      </div>
                    )}
                    {selectedContact.obfuscated && selectedContactId && (
                      <PurchaseContactDialog
                        contactId={selectedContactId}
                        isTrialPeriod={trialPeriod}
                        onPurchase={purchaseContact}
                        buttonText="Vis oplysninger"
                        buttonClassName="w-fit"
                        buttonIntent="primary"
                      />
                    )}
                    {(selectedContact.paths?.length > 0 ||
                      selectedContact.links?.length > 0) &&
                      !selectedContact.obfuscated && (
                        <div>
                          <div className="text-theme-on-background-variant my-2 text-xs">
                            Kilder
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedContact.paths?.[0] && (
                              <div className="border-theme-outline rounded-md border px-2 py-1 transition-all">
                                <span className="text-xs">Website</span>
                              </div>
                            )}
                            {selectedContact.links?.[0] && (
                              <div className="border-theme-outline rounded-md border px-2 py-1">
                                <span className="text-xs">Linkedin</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    {(selectedContact.paths?.length > 0 ||
                      selectedContact.links?.length > 0) &&
                      !selectedContact.obfuscated && (
                        <div>
                          <div className="text-theme-on-background-variant my-2 text-xs">
                            Links
                          </div>
                          <div className="flex flex-col gap-2">
                            {[
                              ...selectedContact.links,
                              ...selectedContact.paths,
                            ].map((link, i) => (
                              <div
                                key={i}
                                className="hover:text-theme-active flex max-w-md items-center gap-x-2"
                              >
                                {link.includes("linkedin") && (
                                  <img
                                    src={
                                      currentTheme === "dark"
                                        ? LinkedInWhite
                                        : LinkedInBlack
                                    }
                                    className="w-4 align-middle"
                                  />
                                )}

                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs transition-all duration-100 hover:underline"
                                >
                                  {link
                                    .replace("https://", "")
                                    .replace("http://", "")
                                    .replace("www.", "")}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    {!selectedContact.obfuscated &&
                      lead?.lead.contactLoadTime &&
                      selectedContactId == lead.lead.contact?.id && (
                        <div>
                          <div className="text-theme-on-background-variant my-2 text-xs">
                            Sidst opdateret
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs">
                              {formatDate(lead.lead.contactLoadTime)}
                            </span>
                            <RACButton
                              onPress={() =>
                                onRefreshContact(selectedContactId)
                              }
                              className="text-theme-on-background hover:text-theme-active cursor-pointer transition-colors"
                            >
                              <RefreshCw
                                size={14}
                                className={
                                  refreshingContactIds.has(selectedContactId)
                                    ? "animate-spin"
                                    : ""
                                }
                              />
                            </RACButton>
                          </div>
                        </div>
                      )}
                  </div>
                </motion.div>
              )
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function LiveNumbers({
  phoneNumbers,
  onClose,
  setShowTrialCTADialog,
  handleCopy,
  onPurchaseLiveNumbers,
}: {
  phoneNumbers?: PhoneNumbersResponse;
  onClose?: () => void;
  setShowTrialCTADialog: (show: boolean) => void;
  handleCopy: (text: string) => void;
  onPurchaseLiveNumbers: () => void;
}) {
  const trialPeriod = useTrialPeriod();
  const { useLeadQuery } = Route.useRouteContext();

  const { data: lead } = useLeadQuery();

  const liveNumber = useMemo(() => {
    return phoneNumbers?.numbers.find(liveNumberFindCb);
  }, [phoneNumbers]);

  const liveNumberIndex = useMemo(() => {
    return phoneNumbers?.numbers.findIndex(liveNumberFindCb) ?? -1;
  }, [phoneNumbers]);

  const hasObfuscatedNumbers = useMemo(() => {
    return phoneNumbers?.numbers.some((num) => num.obfuscated) ?? false;
  }, [phoneNumbers]);

  const [selectedIndex, setSelectedIndex] = useState<number>(
    liveNumberIndex !== -1 ? liveNumberIndex : 0,
  );

  const selectedPhoneNumber = useMemo(() => {
    return phoneNumbers?.numbers[selectedIndex] ?? null;
  }, [phoneNumbers, selectedIndex]);

  const [openDialog, setOpenDialog] = useState(false);

  // const onPhoneNumberFeedback = useCallback(() => {
  //   if (!selectedPhoneNumber) return;

  //   const feedback = prompt("Hvad oplever du af fejl med nummeret?");

  //   if (!feedback) return;

  //   const feedbackInfo = {
  //     page: "Lasso Live: Telefonnummer feedback",
  //     name: user.name,
  //     email: user.email,
  //     phone: user.phoneNumber,
  //     message: feedback,
  //     partnerType: null,
  //     companyName: user.organization.name,
  //     supportSubject: `Jeg har oplevet problemer med dette nummer: ${selectedPhoneNumber.phoneNumber}`,
  //     support: true,
  //   };

  //   if (import.meta.env.DEV || true) {
  //     console.info("[DRY RUN] POST: /contact/contact", feedbackInfo);
  //   }
  //   toast.success("Tak for at rapportere din oplevelse!", {
  //     description:
  //       import.meta.env.DEV || true
  //         ? "Besked ikke sendt i udviklingsmiljø. Tjek konsollen."
  //         : undefined,
  //   });
  // }, [user, selectedPhoneNumber]);

  const hasWebsiteSource = useMemo(
    () =>
      selectedPhoneNumber?.sources.find((s) => s.type === Type.Website) !==
      undefined,
    [selectedPhoneNumber],
  );

  const hasCvrSource = useMemo(
    () =>
      selectedPhoneNumber?.sources.find((s) => s.type === Type.CVR) !==
      undefined,
    [selectedPhoneNumber],
  );

  const hasTeledataSource = useMemo(
    () =>
      selectedPhoneNumber?.sources.find((s) => s.type === Type.Teledata) !==
      undefined,
    [selectedPhoneNumber],
  );

  const teledataSource = useMemo(
    () => selectedPhoneNumber?.sources.find((s) => s.type === Type.Teledata),
    [selectedPhoneNumber],
  );

  const googleSource = useMemo(
    () => selectedPhoneNumber?.sources.find((s) => s.type === Type.Google),
    [selectedPhoneNumber],
  );

  const selectedPhoneNumberLinks = useMemo(() => {
    if (!selectedPhoneNumber) return undefined;

    return selectedPhoneNumber?.sources?.find((s) => !!s.links);
  }, [selectedPhoneNumber]);

  return (
    <div className="flex h-160 w-full flex-col gap-y-6 overflow-hidden px-14 py-16">
      <div className="flex h-4 w-full items-center justify-between">
        <Heading className="text-xs uppercase">telefonnumre</Heading>

        <RACButton
          className="cursor-pointer"
          onPress={() => {
            setSelectedIndex(-1);
            onClose?.();
          }}
        >
          <X />
        </RACButton>
      </div>
      <Separator className="my-6 w-full" />

      <div className="flex h-0 w-full grow">
        <div className="flex h-full w-2/5 shrink grow-0 flex-col">
          {hasObfuscatedNumbers && (
            <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-4">
              <span className="text-sm">Vil du se alle numre?</span>
              <PurchaseLiveNumberDialog
                isTrialPeriod={!!trialPeriod}
                onPurchase={onPurchaseLiveNumbers}
              />
            </div>
          )}
          <div className="-my-3 flex h-0 grow flex-col overflow-y-auto">
            {phoneNumbers &&
              phoneNumbers.numbers.map((num, index) => (
                <div
                  className="group/phone flex h-12 shrink-0 items-center gap-x-4 rounded-md py-3"
                  key={index}
                >
                  <RACButton
                    className="flex items-center gap-x-2"
                    onPress={() => setSelectedIndex(index)}
                  >
                    <div
                      className={twMerge(
                        "text-sm font-bold transition-colors",
                        !num.obfuscated &&
                          "group-hover/phone:text-theme-active cursor-pointer",
                        selectedIndex === index ? "text-theme-active" : "",
                      )}
                    >
                      {num.obfuscated
                        ? formatPhoneNumber(censorPhoneNumber(num.phoneNumber))
                        : formatPhoneNumber(num.phoneNumber)}
                    </div>
                  </RACButton>

                  {index === liveNumberIndex && (
                    <div className="text-xs">Live Nummer</div>
                  )}

                  {!num.obfuscated && num.sources.length === 1 && (
                    <div className="text-xs">{num.sources[0].note}</div>
                  )}
                </div>
              ))}
          </div>
        </div>

        <Separator orientation="vertical" className="mr-14 ml-4" />

        <div
          className={twMerge(
            "flex w-3/5 shrink-0",
            selectedPhoneNumber ? "" : "invisible",
          )}
        >
          <div className="flex w-full flex-col">
            <div className="flex items-center justify-between">
              <span className="text-base font-medium">
                {selectedPhoneNumber &&
                  formatPhoneNumber(selectedPhoneNumber.phoneNumber)}
              </span>

              {!selectedPhoneNumber?.obfuscated && (
                <div className="mr-auto ml-4 flex items-center gap-x-4">
                  <RACButton
                    className="hover:text-theme-active cursor-pointer transition-colors"
                    onPress={() => {
                      if (trialPeriod) {
                        setShowTrialCTADialog(true);
                        return;
                      }

                      if (selectedPhoneNumber?.phoneNumber)
                        handleCopy(selectedPhoneNumber.phoneNumber);
                    }}
                  >
                    <Copy size={16} />
                  </RACButton>

                  <a
                    className="hover:text-theme-active cursor-pointer transition-colors"
                    {...(trialPeriod
                      ? {
                          onClick: () => setShowTrialCTADialog(true),
                        }
                      : { href: `tel:${selectedPhoneNumber?.phoneNumber}` })}
                  >
                    <Phone size={16} />
                  </a>
                </div>
              )}

              <div className="relative">
                <MenuTrigger isOpen={openDialog} onOpenChange={setOpenDialog}>
                  <RACButton className="hover:text-theme-active cursor-pointer text-xs transition-colors">
                    Problemer med telefonnummeret?
                  </RACButton>
                  {lead && (
                    <ReportErrorDialog
                      modalType="phone"
                      selectedPhoneNumber={selectedPhoneNumber?.phoneNumber}
                      lead={lead.lead}
                      isOpen={openDialog}
                      onOpenChange={setOpenDialog}
                      popoverProps={{
                        placement: "left",
                        offset: 40,
                        containerPadding: 10,
                      }}
                    />
                  )}
                </MenuTrigger>
              </div>
            </div>

            {selectedPhoneNumber?.phoneNumber === liveNumber?.phoneNumber && (
              <div className="mt-6 text-xs">
                Live Nummeret er et hovednummer til virksomheden, som du gerne
                må ringe til.
              </div>
            )}

            <div className="border-theme-outline-variant mt-6 mb-8 flex items-stretch rounded-lg border text-center">
              <div className="flex items-center p-7">
                {selectedPhoneNumber?.callable ? (
                  <ShieldCheck
                    size={32}
                    absoluteStrokeWidth
                    className="text-[#B3EF8B]"
                  />
                ) : (
                  <ShieldX
                    size={32}
                    absoluteStrokeWidth
                    className="text-red-500"
                  />
                )}
              </div>

              <Separator className="h-full" orientation="vertical" />

              <div className="flex w-0 grow flex-col justify-center gap-y-1 p-7 text-left text-xs">
                <div className="text-theme-on-background-variant font-medium">
                  {selectedPhoneNumber?.callable
                    ? "Du må ringe på dette nummer"
                    : "Du må ikke ringe på dette nummer"}
                </div>

                <div className="font-normal">
                  {selectedPhoneNumber?.obfuscated &&
                  selectedPhoneNumber?.callable
                    ? "Vis telefonnummeret for at se opkaldsgrundlaget"
                    : selectedPhoneNumber?.explanation}
                </div>
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-y-4 overflow-y-auto">
              <div>
                <div className="text-theme-on-background-variant mb-2 text-xs">
                  Kilder
                </div>

                <div className="flex w-full gap-x-2">
                  {selectedPhoneNumber?.obfuscated ? (
                    <div className="text-xs">
                      Se{" "}
                      {`${selectedPhoneNumber.sources.length} ${selectedPhoneNumber.sources.length === 1 ? "kilde" : "kilder"} ved at vise telefonnummeret`}
                    </div>
                  ) : (
                    selectedPhoneNumber?.sources.map((source, index) => (
                      <div key={index}>
                        <div className="border-theme-outline-variant rounded-md border px-2 py-1 text-xs font-medium">
                          {source.type === Type.Google
                            ? "Internetsøgning"
                            : source.type}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {!selectedPhoneNumber?.obfuscated &&
                hasTeledataSource &&
                teledataSource && (
                  <div className="flex flex-col">
                    <div className="text-theme-on-background-variant mb-2 text-xs">
                      Teleoperatørerne
                    </div>

                    <div className="text-xs">
                      {teledataSource.note
                        ? `${teledataSource.note} - registreret d. ${formatDate(
                            teledataSource.timestamp,
                          )}`
                        : `Registreret d. ${formatDate(
                            teledataSource.timestamp,
                          )}`}
                    </div>
                  </div>
                )}

              {!selectedPhoneNumber?.obfuscated &&
                googleSource &&
                googleSource.timestamp && (
                  <div className="flex flex-col">
                    <div className="text-theme-on-background-variant mb-2 text-xs">
                      Internetsøgning
                    </div>

                    <div className="text-xs">
                      Foretaget d. {formatDate(googleSource.timestamp)}
                    </div>
                  </div>
                )}

              {!selectedPhoneNumber?.obfuscated &&
                (selectedPhoneNumberLinks?.links?.length || hasCvrSource) && (
                  <div className="flex flex-col items-start">
                    <div className="text-theme-on-background-variant mb-2 text-xs">
                      Links
                    </div>

                    {selectedPhoneNumberLinks?.links?.map((link, index) => (
                      <a
                        key={index}
                        href={link}
                        target="_blank"
                        className="hover:text-theme-active text-xs hover:underline"
                      >
                        {link}
                      </a>
                    ))}

                    {hasCvrSource && (
                      <a
                        href={`https://datacvr.virk.dk/enhed/virksomhed/${
                          phoneNumbers?.lassoId.split("CVR-1-")[1]
                        }`}
                        target="_blank"
                        className="hover:text-theme-active text-xs hover:underline"
                      >
                        Se nummeret på CVR
                      </a>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
